/* eslint-disable react-refresh/only-export-components --
 * AuthProvider and useAuth are deliberately co-located: the hook is tightly
 * coupled to the context and splitting would fragment the auth API. The minor
 * Fast Refresh cost is acceptable for a rarely-edited auth module.
 */
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router";

import { useCurrentUserApi } from "@/api/use-current-user-api";
import type { User } from "@/types/api";

interface AuthContextValue {
  user: User | undefined;
  token: string | null;
  isAuthenticated: boolean;
  isAuthorized: boolean;
  isLoading: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_KEY = "auth_token";

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const queryClient = useQueryClient();

  // Handle OAuth callback token from URL (?token=<base64("Bearer <jwt>")>)
  useEffect(() => {
    const tokenParam = searchParams.get("token");
    if (!tokenParam) return;

    // Always strip the query param — we never want a token lingering in the URL.
    const clearParam = (): void => {
      setSearchParams({}, { replace: true });
    };

    try {
      const decoded = window.atob(tokenParam);
      if (!decoded.startsWith("Bearer ")) {
        console.error("OAuth callback: decoded token is not a Bearer token");
        clearParam();
        return;
      }
      const bearerToken = decoded.slice("Bearer ".length).trim();
      if (!bearerToken) {
        console.error("OAuth callback: empty bearer token");
        clearParam();
        return;
      }
      localStorage.setItem(TOKEN_KEY, bearerToken);
      setToken(bearerToken);
    } catch (error) {
      console.error("OAuth callback: failed to decode token", error);
    } finally {
      clearParam();
    }
  }, [searchParams, setSearchParams]);

  const { user, isLoading } = useCurrentUserApi({ enabled: !!token });
  // Note: a 401 from any request is handled centrally by the apiClient
  // interceptor (clears token + redirects to /login). No need to duplicate
  // that logic here, and clearing on every query error would sign users out
  // on transient network blips.

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    queryClient.clear();
  }, [queryClient]);

  const isAuthenticated = !!token;
  const isAuthorized = isAuthenticated && user?.isAbodoSuperAdmin === true;

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated,
      isAuthorized,
      isLoading: isAuthenticated && isLoading,
      logout,
    }),
    [user, token, isAuthenticated, isAuthorized, isLoading, logout],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
